import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, ArrowRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Logo } from "./Logo";
import { NAV_LINKS, PRODUCTS } from "../data/content";

const linkCls = ({ isActive }) =>
  `text-sm font-medium transition-colors duration-150 hover:text-blue-700 ${
    isActive ? "text-blue-700" : "text-slate-700"
  }`;

export const Header = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const productsActive = pathname.startsWith("/products");

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-slate-200/70 bg-white/80 backdrop-blur-md">
      <div className="container-x flex h-16 items-center justify-between gap-4">
        <Link to="/" aria-label="ZasyaOnline home" data-testid="header-logo">
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
          <NavLink to="/" end className={linkCls} data-testid="nav-home-link">
            Home
          </NavLink>
          <div className="relative group">
            <NavLink
              to="/products"
              className={() =>
                `inline-flex items-center gap-1 text-sm font-medium transition-colors duration-150 hover:text-blue-700 ${
                  productsActive ? "text-blue-700" : "text-slate-700"
                }`
              }
              data-testid="nav-products-link"
              aria-haspopup="true"
            >
              Products
              <ChevronDown size={14} aria-hidden="true" />
            </NavLink>
            <div className="invisible absolute left-1/2 top-full z-50 w-64 -translate-x-1/2 pt-3 opacity-0 transition-all duration-150 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
              <div className="card-line p-2 shadow-xl shadow-slate-900/5">
                {PRODUCTS.map((p) => (
                  <Link
                    key={p.id}
                    to={p.path}
                    data-testid={`nav-dropdown-${p.id}`}
                    className="block rounded-xl px-3 py-2.5 transition-colors hover:bg-slate-50 focus-visible:bg-slate-50"
                  >
                    <span className="block text-sm font-semibold text-slate-900">{p.name}</span>
                    <span className="block text-xs text-slate-500">{p.category}</span>
                  </Link>
                ))}
                <Link
                  to="/products"
                  data-testid="nav-dropdown-all-products"
                  className="mt-1 flex items-center gap-1.5 border-t border-slate-100 px-3 pt-2.5 pb-1.5 text-xs font-semibold text-blue-700"
                >
                  All products <ArrowRight size={12} aria-hidden="true" />
                </Link>
              </div>
            </div>
          </div>
          {NAV_LINKS.slice(2).map((l) => (
            <NavLink key={l.path} to={l.path} className={linkCls} data-testid={l.testId}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link to="/contact" className="btn-primary hidden !px-5 !py-2.5 sm:inline-flex" data-testid="nav-contact-cta">
            Get in Touch
          </Link>
          <button
            type="button"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 lg:hidden"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            data-testid="mobile-menu-button"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="overflow-hidden border-t border-slate-200/70 bg-white lg:hidden"
            aria-label="Mobile"
            data-testid="mobile-menu"
          >
            <div className="container-x flex flex-col gap-1 py-4">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.path}
                  to={l.path}
                  end={l.path === "/"}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `rounded-xl px-3 py-2.5 text-sm font-medium ${
                      isActive ? "bg-blue-50 text-blue-700" : "text-slate-700"
                    }`
                  }
                  data-testid={`mobile-${l.testId}`}
                >
                  {l.label}
                </NavLink>
              ))}
              {PRODUCTS.map((p) => (
                <NavLink
                  key={p.id}
                  to={p.path}
                  onClick={() => setOpen(false)}
                  className="rounded-xl px-3 py-2 pl-7 text-sm text-slate-500"
                  data-testid={`mobile-nav-${p.id}`}
                >
                  {p.name}
                </NavLink>
              ))}
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                className="btn-primary mt-2"
                data-testid="mobile-nav-contact-cta"
              >
                Get in Touch
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
